// Components
import { Indicator, UnstyledButton } from "@mantine/core";
import { IconBell } from "@tabler/icons-react";
import { ClinicalNotificationDrawer } from "./ClinicalNotificationDrawer";

// Hooks
import { useMemo, useState } from "react";
import { useCurrentUserOptional } from "@/app/auth/CurrentUserContext";
import { useNotificationTaskCount } from "@/hooks/domain/useNotificationTaskCount";

/**
 * Props for {@link ClinicalNotificationBell}.
 * @agentic connections
 */
export type ClinicalNotificationBellProps = {
  collapsed?: boolean;
  className?: string;
  /**
   * Applied to the icon's wrapping element. Mantine's `Indicator` root
   * renders as a block box, which puts the bell SVG in an inline formatting
   * context and leaves it sitting slightly above true center (line-height
   * whitespace below the icon). This class should force `display: flex;
   * align-items: center;` to keep the icon centered regardless.
   */
  iconClassName?: string;
};

/**
 * Sidebar notification bell with an accessible unread count and the
 * notification center drawer. Renders a bell with zero count when the current
 * user has no resolvable roles, so the shell always renders.
 * @agentic connections
 */
export const ClinicalNotificationBell = ({
  collapsed = false,
  className,
  iconClassName,
}: ClinicalNotificationBellProps) => {
  const currentUser = useCurrentUserOptional();
  const [opened, setOpened] = useState(false);

  const ownerRoleIds = useMemo(
    () =>
      (currentUser?.practitionerRoles ?? [])
        .map((role) => role.id)
        .filter((id): id is string => Boolean(id)),
    [currentUser],
  );

  const { count } = useNotificationTaskCount({ ownerRoleIds });

  const primaryRoleId = currentUser?.primaryPractitionerRole?.id;
  const label = count > 0 ? `Notifications (${count} unread)` : "Notifications";

  return (
    <>
      <UnstyledButton
        className={className}
        onClick={() => setOpened(true)}
        aria-label={label}
        data-testid="clinical-notification-bell"
      >
        <Indicator
          className={iconClassName}
          disabled={count === 0}
          label={count > 99 ? "99+" : count}
          size={16}
          color="red"
          offset={4}
          aria-hidden
        >
          <IconBell size={16} />
        </Indicator>
        {!collapsed && <span>Notifications</span>}
      </UnstyledButton>

      <ClinicalNotificationDrawer
        opened={opened}
        onClose={() => setOpened(false)}
        ownerRoleIds={ownerRoleIds}
        actingRoleReference={
          primaryRoleId ? `PractitionerRole/${primaryRoleId}` : undefined
        }
      />
    </>
  );
};
